import { useEffect, useState } from "react";

const LAKES_KEY = "catchcompass:offline-lakes:v1";
const SERVER_SESSIONS_KEY = "catchcompass:offline-server-sessions:v1";
const SESSIONS_KEY = "catchcompass:offline-sessions:v1";
export const OFFLINE_AUTH_KEY = "catchcompass:offline-auth:v1";

export type OfflineAction =
  | {
      id: string;
      kind: "event";
      occurred_at: string;
      rod_number: number;
      event_type: "lost_fish" | "aborted_take" | "knock" | "liner" | "recast" | "rod_moved";
      species?: "carp" | "catfish" | "unknown" | null;
      spot_snapshot?: string | null;
      distance_m?: number | null;
      depth_ft?: number | null;
      notes?: string | null;
      synced?: boolean;
    }
  | {
      id: string;
      kind: "setup";
      occurred_at: string;
      rod_number: number;
      rig_snapshot?: string | null;
      feed_bait_snapshot?: string | null;
      hookbait_snapshot?: string | null;
      additive_snapshot?: string | null;
      baiting_approach?: string | null;
      synced?: boolean;
    }
  | {
      id: string;
      kind: "catch";
      occurred_at: string;
      rod_number: number;
      catch_species: "carp" | "catfish";
      weight_lb: number | null;
      weight_oz: number | null;
      synced?: boolean;
    };

export type OfflineSession = {
  id: string;
  server_id?: number | null;
  lake_id: number;
  lake_name: string;
  peg: string;
  start_at: string;
  end_at: string | null;
  rods_count: number;
  target_species: "carp" | "catfish" | "both";
  privacy_setting: string;
  share_peg_anonymously: boolean;
  advisor_recommendation_id?: number | string | null;
  payload: Record<string, unknown>;
  actions: OfflineAction[];
  created_at: string;
};

function safeParse<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeSessions(sessions: OfflineSession[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  window.dispatchEvent(new CustomEvent("catchcompass:offline-changed"));
}

export function cacheOfflineLakes(lakes: any[]) {
  if (typeof window === "undefined" || !Array.isArray(lakes) || !lakes.length) return;
  window.localStorage.setItem(LAKES_KEY, JSON.stringify(lakes));
}

export function readOfflineLakes(): any[] {
  return safeParse<any[]>(LAKES_KEY, []);
}

export function cacheServerSessions(sessions: any[]) {
  if (typeof window === "undefined" || !Array.isArray(sessions)) return;
  window.localStorage.setItem(SERVER_SESSIONS_KEY, JSON.stringify(sessions));
}

export function readCachedServerSessions(): any[] {
  return safeParse<any[]>(SERVER_SESSIONS_KEY, []);
}

export function listOfflineSessions(): OfflineSession[] {
  return safeParse<OfflineSession[]>(SESSIONS_KEY, []);
}

export function readOfflineSession(id: string): OfflineSession | null {
  return listOfflineSessions().find((session) => session.id === id) ?? null;
}

export function createOfflineSession(
  payload: Record<string, any>,
  lakeName: string,
): OfflineSession {
  const id = `offline-${crypto.randomUUID()}`;
  const record: OfflineSession = {
    id,
    server_id: null,
    lake_id: Number(payload.lake_id),
    lake_name: lakeName,
    peg: String(payload.peg ?? ""),
    start_at: String(payload.start_at),
    end_at: null,
    rods_count: Math.max(1, Number(payload.rods_count || 1)),
    target_species:
      payload.target_species === "carp" || payload.target_species === "catfish"
        ? payload.target_species
        : "both",
    privacy_setting:
      String(payload.privacy_setting || "Contribute anonymously to CarpIntel statistics"),
    share_peg_anonymously: Boolean(payload.share_peg_anonymously),
    advisor_recommendation_id: payload.advisor_recommendation_id ?? null,
    payload: { ...payload, end_at: null },
    actions: [],
    created_at: new Date().toISOString(),
  };
  writeSessions([record, ...listOfflineSessions()]);
  return record;
}

export function saveOfflineSession(next: OfflineSession) {
  const sessions = listOfflineSessions();
  const index = sessions.findIndex((session) => session.id === next.id);
  if (index >= 0) sessions[index] = next;
  else sessions.unshift(next);
  writeSessions(sessions);
}

export function addOfflineAction(sessionId: string, action: Omit<OfflineAction, "id">) {
  const session = readOfflineSession(sessionId);
  if (!session) return null;
  const nextAction = {
    ...action,
    id: crypto.randomUUID(),
  } as OfflineAction;
  session.actions.push(nextAction);
  saveOfflineSession(session);
  return nextAction;
}

export function updateOfflineCatchWeight(
  sessionId: string,
  actionId: string,
  weightLb: number | null,
  weightOz: number | null,
) {
  const session = readOfflineSession(sessionId);
  if (!session) return;
  session.actions = session.actions.map((action) =>
    action.id === actionId && action.kind === "catch"
      ? { ...action, weight_lb: weightLb, weight_oz: weightOz }
      : action,
  );
  saveOfflineSession(session);
}

export function endOfflineSession(sessionId: string, endedAt = new Date().toISOString()) {
  const session = readOfflineSession(sessionId);
  if (!session) return;
  session.end_at = endedAt;
  saveOfflineSession(session);
}

export function useOnlineStatus() {
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return online;
}

function actionPriority(action: OfflineAction) {
  if (action.kind === "setup") return 0;
  if (action.kind === "event" && action.event_type === "rod_moved") return 1;
  if (action.kind === "event") return 2;
  return 3;
}

async function jsonFetch(path: string, init?: RequestInit) {
  const response = await fetch(path, init);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(String(body?.error || `Sync failed (${response.status})`));
  }
  return body;
}

const syncing = new Set<string>();

export async function syncOfflineSession(
  sessionId: string,
): Promise<number | null> {
  if (typeof navigator !== "undefined" && !navigator.onLine) return null;
  if (syncing.has(sessionId)) return null;
  syncing.add(sessionId);
  try {
    let session = readOfflineSession(sessionId);
    if (!session) return null;

    if (!session.server_id) {
      const created = await jsonFetch("/app-api/sessions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...session.payload,
          lake_id: session.lake_id,
          peg: session.peg,
          start_at: session.start_at,
          end_at: null,
          rods_count: session.rods_count,
          target_species: session.target_species,
          privacy_setting: session.privacy_setting,
          share_peg_anonymously: session.share_peg_anonymously,
          advisor_recommendation_id: session.advisor_recommendation_id ?? null,
        }),
      });
      session.server_id = Number(created.id);
      saveOfflineSession(session);
    }

    const serverId = Number(session.server_id);
    const anglers = await jsonFetch(`/app-api/sessions/${serverId}/anglers`);
    const owner = Array.isArray(anglers)
      ? anglers.find((angler: any) => angler.is_owner)
      : null;
    if (!owner?.id) throw new Error("Could not resolve the session owner while syncing");

    const actions = [...session.actions].sort((a, b) => {
      const time = new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime();
      return time || actionPriority(a) - actionPriority(b);
    });

    for (const action of actions) {
      if (action.synced) continue;

      if (action.kind === "setup") {
        await jsonFetch(`/app-api/sessions/${serverId}/rod-setups`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_angler_id: Number(owner.id),
            rod_number: action.rod_number,
            started_at: action.occurred_at,
            rig_snapshot: action.rig_snapshot ?? null,
            feed_bait_snapshot: action.feed_bait_snapshot ?? null,
            hookbait_snapshot: action.hookbait_snapshot ?? null,
            additive_snapshot: action.additive_snapshot ?? null,
            baiting_approach: action.baiting_approach ?? null,
            bait_amount: null,
            bait_unit: "grams",
          }),
        });
      } else if (action.kind === "event") {
        await jsonFetch(`/app-api/sessions/${serverId}/events`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_angler_id: Number(owner.id),
            rod_number: action.rod_number,
            event_type: action.event_type,
            species: action.species ?? null,
            spot_snapshot: action.spot_snapshot ?? null,
            distance_m: action.distance_m ?? null,
            depth_ft: action.depth_ft ?? null,
            notes: action.notes ?? null,
            occurred_at: action.occurred_at,
          }),
        });
      } else {
        await jsonFetch("/app-api/catches", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: serverId,
            session_angler_id: Number(owner.id),
            caught_at: action.occurred_at,
            species: action.catch_species === "catfish" ? "Catfish" : "Carp",
            catch_species: action.catch_species,
            carp_type: "Unknown",
            weight_lb: action.weight_lb,
            weight_oz: action.weight_oz,
            rod_number: action.rod_number,
            bait_unit: "grams",
            privacy_setting: session.privacy_setting,
            share_peg_anonymously: session.share_peg_anonymously,
          }),
        });
      }

      session = readOfflineSession(sessionId) ?? session;
      session.actions = session.actions.map((stored) =>
        stored.id === action.id ? { ...stored, synced: true } : stored,
      );
      saveOfflineSession(session);
    }

    session = readOfflineSession(sessionId) ?? session;
    if (session.end_at) {
      await jsonFetch(
        `/app-api/sessions/${serverId}/end?ended_at=${encodeURIComponent(session.end_at)}`,
        { method: "POST" },
      );
    }

    const remaining = listOfflineSessions().filter((item) => item.id !== sessionId);
    writeSessions(remaining);
    window.dispatchEvent(
      new CustomEvent("catchcompass:offline-synced", {
        detail: { offlineId: sessionId, sessionId: serverId },
      }),
    );
    return serverId;
  } finally {
    syncing.delete(sessionId);
  }
}

export async function syncAllOfflineSessions() {
  if (typeof navigator !== "undefined" && !navigator.onLine) return [];
  const results: Array<{ offlineId: string; sessionId: number }> = [];
  for (const session of listOfflineSessions()) {
    try {
      const realId = await syncOfflineSession(session.id);
      if (realId) results.push({ offlineId: session.id, sessionId: realId });
    } catch {
      // Keep the local record. A later online event can retry safely.
    }
  }
  return results;
}
